import { Badge, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"

export type SendVendorOptionStatus = "connected" | "not-connected" | "inactive" | "needs-resign"

/** Keeps vendor name/description aligned whether or not a logo exists. */
const LOGO_SLOT_CLASS = "size-9 shrink-0 rounded-lg"

export interface SendVendorOptionCardProps {
  vendorName: string
  logoUrl: string | null
  description: string | null
  selected: boolean
  status: SendVendorOptionStatus
  onSelect: () => void
}

function VendorLogoSlot({
  logoUrl,
  selected,
}: {
  logoUrl: string | null
  selected: boolean
}) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        loading="lazy"
        className={cn(
          LOGO_SLOT_CLASS,
          "border border-border/60 bg-background object-contain p-1",
        )}
      />
    )
  }

  return (
    <span
      aria-hidden
      className={cn(
        LOGO_SLOT_CLASS,
        "bg-gradient-to-r to-transparent",
        selected
          ? "from-primary/14 via-primary/6"
          : "from-muted/80 via-muted/30",
      )}
    />
  )
}

export function SendVendorOptionCard({
  vendorName,
  logoUrl,
  description,
  selected,
  status,
  onSelect,
}: SendVendorOptionCardProps) {
  const { t } = useTranslation()

  const badgeLabel =
    status === "connected"
      ? t("opportunities.scripts.sendDialog.connectedBadge")
      : status === "needs-resign"
        ? t("marketplace.resign.badge")
        : status === "inactive"
          ? t("opportunities.scripts.sendDialog.inactiveBadge")
          : t("opportunities.scripts.sendDialog.notConnectedBadge")

  const badgeVariant =
    status === "connected"
      ? "status-connected"
      : status === "needs-resign"
        ? "status-in-production"
        : "secondary"

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full cursor-pointer rounded-lg border px-4 py-3 text-left transition-[border-color,box-shadow,background-color] duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        selected
          ? "border-primary bg-primary/5 shadow-sm"
          : "border-border bg-background hover:border-primary/30 hover:bg-muted/20",
      )}
    >
      <div className="flex gap-3">
        <VendorLogoSlot logoUrl={logoUrl} selected={selected} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-semibold leading-snug text-foreground">{vendorName}</p>
            <Badge variant={badgeVariant} className="shrink-0">
              {badgeLabel}
            </Badge>
          </div>
          {description && (
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
              {description}
            </p>
          )}
          {selected && status === "not-connected" && (
            <p className="mt-2 text-xs text-muted-foreground">
              {t("opportunities.scripts.sendDialog.connectFirstHint")}
            </p>
          )}
        </div>
      </div>
    </button>
  )
}

export function SendVendorOptionCardSkeleton() {
  return (
    <div className="rounded-lg border border-border px-4 py-3">
      <div className="flex gap-3">
        <div className="size-9 shrink-0 animate-pulse rounded-lg bg-muted" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="h-4 w-28 animate-pulse rounded bg-muted" />
            <div className="h-5 w-20 animate-pulse rounded-full bg-muted" />
          </div>
          <div className="mt-2 h-3 w-full animate-pulse rounded bg-muted" />
        </div>
      </div>
    </div>
  )
}
